import { sortCalendarsByName } from '@griply/utils';
import { useExternalCalendarConnectionsWithCalendars } from 'features/external-connections';
import { useMemo } from 'react';
import { ConnectionCalendar } from 'shared/types/external-calendar';
import { ID } from 'shared/types/id';
import { ViewOptionsCalendarAccount } from 'shared/types/view-options-section';

export const useCalendarAccountSections = (): ViewOptionsCalendarAccount[] => {
  const { connections, calendars, isPending } =
    useExternalCalendarConnectionsWithCalendars();

  return useMemo(() => {
    if (isPending) {
      return [];
    }

    // Blacklisted calendars are filtered out server-side, so they cannot be made visible here.
    const blacklists = new Map<ID, ID[]>(
      connections.map((connection) => [
        connection.id,
        connection.settings?.calendarBlacklist ?? [],
      ]),
    );

    const grouped = calendars.reduce<Map<ID, ConnectionCalendar[]>>(
      (map, calendar) => {
        if (blacklists.get(calendar.connectionId)?.includes(calendar.id)) {
          return map;
        }

        const current = map.get(calendar.connectionId) ?? [];
        map.set(calendar.connectionId, [...current, calendar]);

        return map;
      },
      new Map(),
    );

    return connections.reduce<ViewOptionsCalendarAccount[]>(
      (accounts, connection) => {
        const connectionCalendars = grouped.get(connection.id);
        if (!connectionCalendars?.length) {
          return accounts;
        }

        return [
          ...accounts,
          {
            id: connection.id,
            title: connection.providerInfo.email,
            calendars: sortCalendarsByName(connectionCalendars),
          },
        ];
      },
      [],
    );
  }, [calendars, connections, isPending]);
};
